import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { CreateOrderRequestDto, CreateOrderResponseDto, OrderDetailsResponseDto, OrderStatus, UpdateOrderRequestDto, UpdateOrderResponseDto } from '@/features/orders/types/orders-dto';
import EToast from '@/components/everridge/EToast';
import { t } from 'i18next';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = { refetchInterval?: number | false } & ApiHookProps;

export const useOrderDetailsApi = ({ id, refetchInterval }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();

    const {
        data: orderDetailsData, 
        isLoading: isLoadingOrderDetailsData, 
        isError: isErrorOrderDetailsData,       
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.orders.queryKeys.details, 
            id
        ],
        queryFn: async (): Promise<OrderDetailsResponseDto> => {
            const url = `${ApiConfig.queries.features.orders.endpoints.get}/${id}`; 
            const response = await axiosInstance.get<OrderDetailsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
        enabled: !!id,
        refetchInterval: (query) => {
            if (!refetchInterval) return false;
            const status = query.state.data?.status;
            return status === undefined || status === OrderStatus.PendingPayment
                ? refetchInterval
                : false;
        },
    });

    const {
        mutateAsync: createOrder, 
        isPending: isCreatingOrder, 
        isError: isErrorCreatingOrder
    } = useMutation({
        mutationFn: async (dto: CreateOrderRequestDto): Promise<CreateOrderResponseDto> => {
            const response = await axiosInstance.post<CreateOrderResponseDto>(
                ApiConfig.queries.features.orders.endpoints.create, dto
            );  
            return response.data;
        },
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({ 
                queryKey: [
                    ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
                    ApiConfig.queries.features.orders.queryKeys.list, 
                ] 
            });

            // When the order requires online payment, the API returns a redirect URL to the gateway.
            if (data?.redirectUrl) {
                window.location.href = data.redirectUrl;
                return;
            }

            EToast({
                variant: 'success',
                message: t('main.orders.toasts.create.success.message'), 
                description: t('main.orders.toasts.create.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.orders.toasts.create.error.message'), 
                description: t('main.orders.toasts.create.error.description')
            });
        }
    });
    
    const {
        mutateAsync: updateOrder, 
        isPending: isUpdatingOrder, 
        isError: isErrorUpdatingOrder
    } = useMutation({
        mutationFn: async (dto: UpdateOrderRequestDto): Promise<UpdateOrderResponseDto> => {
            const url = `${ApiConfig.queries.features.orders.endpoints.update}/${id}`; 
            const response = await axiosInstance.put<UpdateOrderResponseDto>(url, dto);
            return response.data;
        },
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({ 
                queryKey: [
                    ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
                    ApiConfig.queries.features.orders.queryKeys.list, 
                ]
            });

            if (data?.redirectUrl) {
                window.location.href = data.redirectUrl;
                return;
            }

            EToast({
                variant: 'success',
                message: t('main.orders.toasts.update.success.message'), 
                description: t('main.orders.toasts.update.success.description')
            });  
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.orders.toasts.update.error.message'), 
                description: t('main.orders.toasts.update.error.description')
            });
        }
    });

    const {
        mutateAsync: deleteOrder, 
        isPending: isDeletingOrder, 
        isError: isErrorDeletingOrder
    } = useMutation({
        mutationFn: async (id: string) => {
            const url = `${ApiConfig.queries.features.orders.endpoints.delete}/${id}`;
            const response = await axiosInstance.delete(url);       
            return response.data;            
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [
                    ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
                    ApiConfig.queries.features.orders.queryKeys.list, 
                ]
            });
            EToast({
                variant: 'success', 
                message: t('main.orders.toasts.delete.success.message'), 
                description: t('main.orders.toasts.delete.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error', 
                message: t('main.orders.toasts.delete.error.message'), 
                description: t('main.orders.toasts.delete.error.description')
            });
        }
    });

    const {
        mutateAsync: retryOrderPayment,
        isPending: isRetryingOrderPayment,
    } = useMutation({
        mutationFn: async (): Promise<CreateOrderResponseDto> => {
            const url = `${ApiConfig.queries.features.orders.endpoints.pay}/${id}/pay`;
            const response = await axiosInstance.post<CreateOrderResponseDto>(url);
            return response.data;
        },
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({
                queryKey: [
                    ApiConfig.queries.features.hotels.queryKeys.apiRefresh,
                    ApiConfig.queries.features.orders.queryKeys.list,
                    ApiConfig.queries.features.orders.queryKeys.details,
                    id,
                ],
            });

            if (data?.redirectUrl) {
                window.location.href = data.redirectUrl;
                return;
            }

            EToast({
                variant: 'error',
                message: t('main.orders.toasts.retryPayment.error.message'),
                description: t('main.orders.toasts.retryPayment.error.description'),
            });
        },
        onError: () => {
            EToast({
                variant: 'error',
                message: t('main.orders.toasts.retryPayment.error.message'),
                description: t('main.orders.toasts.retryPayment.error.description'),
            });
        },
    });

    return {
        orderDetailsData,
        isLoadingOrderDetailsData,
        isErrorOrderDetailsData,
        createOrder, 
        isCreatingOrder, 
        isErrorCreatingOrder,
        updateOrder, 
        isUpdatingOrder, 
        isErrorUpdatingOrder,
        deleteOrder,
        isDeletingOrder,
        isErrorDeletingOrder,
        retryOrderPayment,
        isRetryingOrderPayment,
    }
};
